import { useEffect, useState } from 'react'
import { Button, Empty, FloatButton, Skeleton } from 'antd'
import { EyeOutlined, SyncOutlined } from '@ant-design/icons'
import { DefaultTable } from 'diferpan-common-web'
import type { BaseColumn } from 'diferpan-common-web'
import { useAppMessage } from '@providers'
import { useFollowUpOrdensCompraQuery } from '@queries'
import { resolverMensagemErro } from '@utils'
import { ModalItensOrdemCompra } from './ModalItensOrdemCompra'
import { COLUNAS_ORDENS_COMPRA, TEXTOS_ORDENS_COMPRA } from './content'
import { formatarMoeda, formatarQuantidade } from './formatadores'
import type { FollowUpOrdemCompraResponse } from '../../types'
import './style.scss'

const SEM_VALOR = '—'

export const FollowUpPage = () => {
  const message = useAppMessage()
  const [ordemSelecionada, setOrdemSelecionada] = useState<FollowUpOrdemCompraResponse | null>(null)
  const [isModalAberta, setIsModalAberta] = useState(false)

  const ordensQuery = useFollowUpOrdensCompraQuery()

  useEffect(() => {
    if (ordensQuery.error) message.error(resolverMensagemErro(ordensQuery.error, TEXTOS_ORDENS_COMPRA.erroConsulta))
  }, [ordensQuery.error])

  const ordens = ordensQuery.data ?? []
  const hasOrdens = ordens.length > 0
  const isAtualizando = ordensQuery.isFetching && !ordensQuery.isPending

  const handleDetalhesClick = (ordem: FollowUpOrdemCompraResponse) => {
    setOrdemSelecionada(ordem)
    setIsModalAberta(true)
  }

  const handleFecharModal = () => {
    setIsModalAberta(false)
  }

  const handleModalFechada = () => {
    setOrdemSelecionada(null)
  }

  const renderAcoes = (ordem: FollowUpOrdemCompraResponse) => (
    <Button
      type='dashed'
      size='small'
      icon={<EyeOutlined />}
      className={'botao-follow-up-class'}
      aria-label={`${TEXTOS_ORDENS_COMPRA.detalhes} da cotação ${ordem.numero}`}
      onClick={() => handleDetalhesClick(ordem)}
    >
      {TEXTOS_ORDENS_COMPRA.detalhes}
    </Button>
  )

  const colunas: BaseColumn[] = [
    {
      title: COLUNAS_ORDENS_COMPRA.numero,
      dataIndex: 'numero',
      key: 'numero',
      width: 110,
      fixed: 'left',
      sorter: (primeira: FollowUpOrdemCompraResponse, segunda: FollowUpOrdemCompraResponse) =>
        primeira.numero - segunda.numero,
    },
    {
      title: COLUNAS_ORDENS_COMPRA.fatura,
      dataIndex: 'fatura',
      key: 'fatura',
      width: 140,
      searchable: true,
    },
    {
      title: COLUNAS_ORDENS_COMPRA.giro,
      dataIndex: 'giro',
      key: 'giro',
      width: 140,
      searchable: true,
    },
    {
      title: COLUNAS_ORDENS_COMPRA.comprador,
      dataIndex: 'comprador',
      key: 'comprador',
      width: 200,
      searchable: true,
    },
    {
      title: COLUNAS_ORDENS_COMPRA.quantidadeItens,
      dataIndex: 'quantidadeItens',
      key: 'quantidadeItens',
      width: 130,
      align: 'right',
      className: 'tabela-follow-up-class__numero',
      sorter: (primeira: FollowUpOrdemCompraResponse, segunda: FollowUpOrdemCompraResponse) =>
        (primeira.quantidadeItens ?? 0) - (segunda.quantidadeItens ?? 0),
      render: (valor: number | null) => (valor === null ? SEM_VALOR : formatarQuantidade(valor)),
    },
    {
      title: COLUNAS_ORDENS_COMPRA.valorTotal,
      dataIndex: 'valorTotal',
      key: 'valorTotal',
      width: 160,
      align: 'right',
      className: 'tabela-follow-up-class__numero',
      sorter: (primeira: FollowUpOrdemCompraResponse, segunda: FollowUpOrdemCompraResponse) =>
        (primeira.valorTotal ?? 0) - (segunda.valorTotal ?? 0),
      render: (valor: number | null) => (valor === null ? SEM_VALOR : formatarMoeda(valor)),
    },
    {
      title: COLUNAS_ORDENS_COMPRA.acoes,
      key: 'acoes',
      width: 140,
      fixed: 'right',
      render: (_: unknown, ordem: FollowUpOrdemCompraResponse) => renderAcoes(ordem),
    },
  ]

  return (
    <>
      <section className={'regiao-follow-up-class'} aria-label={TEXTOS_ORDENS_COMPRA.tituloRegiao}>
        <section
          className={'cartao-follow-up-class'}
          aria-labelledby='titulo-cotacoes-follow-up'
          aria-busy={isAtualizando}
        >
          <div className={'cartao-follow-up-class__cabecalho'}>
            <h2 className={'cartao-follow-up-class__titulo'} id='titulo-cotacoes-follow-up'>
              {TEXTOS_ORDENS_COMPRA.tituloTabela}
            </h2>
            <span className={'cartao-follow-up-class__dica'}>{TEXTOS_ORDENS_COMPRA.dica}</span>
          </div>
          <Skeleton loading={ordensQuery.isPending} active paragraph={{ rows: 5 }}>
            {hasOrdens ? (
              <DefaultTable
                baseColumns={colunas}
                dataSource={ordens}
                rowKey='numero'
                className={'tabela-follow-up-class'}
                scroll={{ x: 'max-content', y: 'min(64vh, 680px)' }}
                pagination={{ pageSize: 10 }}
              />
            ) : (
              <Empty description={TEXTOS_ORDENS_COMPRA.vazio} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Skeleton>
        </section>
      </section>

      <FloatButton
        icon={<SyncOutlined />}
        tooltip={{ title: TEXTOS_ORDENS_COMPRA.atualizar, placement: 'left' }}
        className={'float-button-top-one-class'}
        style={{ insetBlockStart: 80, insetBlockEnd: 'auto', insetInlineEnd: 10 }}
        aria-label={TEXTOS_ORDENS_COMPRA.atualizar}
        onClick={() => void ordensQuery.refetch()}
      />

      {ordemSelecionada !== null && (
        <ModalItensOrdemCompra
          ordem={ordemSelecionada}
          aberto={isModalAberta}
          onFechar={handleFecharModal}
          onFechada={handleModalFechada}
        />
      )}
    </>
  )
}
